import {HttpClient} from '@angular/common/http'
import {computed, inject, Injectable, signal, WritableSignal} from '@angular/core'
import {
  APPLICATION_ID,
  IApplication,
  IApplicationAdminData,
  IApplicationLog,
  IApplicationSummary,
  IApplicationUpdate
} from '@sparbanken-syd/loan-backend'
import {SparbankenUser} from '@sparbanken-syd/sparbanken-syd-bankid'
import {Observable, tap} from 'rxjs'
import {map} from 'rxjs/operators'
import {environment} from '../../environments/environment'
import {excludedOffices} from '../application/app'
import {LoanPromiseStatus} from './loan-promise-status'


@Injectable({
  providedIn: 'root'
})
export class LoanService {

  public users: WritableSignal<SparbankenUser[]> = signal([])

  public readonly allUsersMap = new Map<string, SparbankenUser>()

  /**
   * Interested parties may read this to have a short list
   * of loan promises to look at.
   */
  public loanPromiseList = signal<IApplicationSummary[]>([])

  /**
   * Updated application details (loan promise data)
   */
  public application = signal<IApplication | null>(null)

  /**
   * Computed loan promise status based on current application
   */
  public loanPromiseStatus = computed(() => {
    const app = this.application()
    return app ? new LoanPromiseStatus(app) : null
  })

  public offices: WritableSignal<string[]> = signal([])

  private readonly httpClient = inject(HttpClient)

  /**
   * Fetch existing loan promise
   * @param id - The ID of the promise
   */
  public fetchComplete(id: string): Observable<IApplication> {
    const url = `${environment.loanServiceUrl}/promise/${id}/complete`
    return this.httpClient.get<IApplication>(url).pipe(
      tap(a => this.application.set(a))
    )
  }

  public updateApplication(application: IApplication, update: IApplicationUpdate): Observable<IApplication> {
    const url = `${environment.loanServiceUrl}/promise/${application[APPLICATION_ID]}`
    return this.httpClient.put<IApplication>(url, {application, update})
      .pipe(tap(a => this.application.set(a)))
  }

  public runUc(id: string): Observable<IApplication> {
    const url = `${environment.loanServiceUrl}/promise/${id}/uc`
    return this.httpClient.put<IApplication>(url, {})
      .pipe(
        tap(a => this.application.set(a))
      )
  }

  public issueLoan(id: string, notes: string): Observable<IApplication> {
    const url = `${environment.loanServiceUrl}/promise/${id}/issue`
    return this.httpClient.put<IApplication>(url, {notes})
      .pipe(tap(a => this.application.set(a)))
  }

  public declineLoan(id: string, notes: string): Observable<IApplication> {
    const url = `${environment.loanServiceUrl}/promise/${id}/decline`
    return this.httpClient.put<IApplication>(url, {notes})
      .pipe(tap(a => this.application.set(a)))
  }


  public delete(id: string): Observable<void> {
    const url = `${environment.loanServiceUrl}/admin/${id}`
    return this.httpClient.delete<void>(url).pipe(
      map(() => {
        this.loanPromiseList.set(this.loanPromiseList()
          .filter(lp => lp['loan-promise-id'] !== id))
        this.application.set(null)
      })
    )
  }

  public list(): Observable<IApplicationSummary[]> {
    const url = `${environment.loanServiceUrl}/admin/list`
    return this.httpClient.get<IApplicationSummary[]>(url)
      .pipe(
        map(list => {
          this.loanPromiseList.set(list)
          return this.loanPromiseList()
        })
      )
  }

  public listReadOnly(id: string): Observable<IApplicationSummary[]> {
    const url = `${environment.loanServiceUrl}/admin/list/read-only`
    return this.httpClient.put<IApplicationSummary[]>(url, {id})
      .pipe(
        map(list => {
          this.loanPromiseList.set(list)
          return this.loanPromiseList()
        })
      )
  }

  public updateAssignee(id: string, data: IApplicationAdminData): Observable<IApplicationSummary> {
    const url = `${environment.loanServiceUrl}/admin/${id}`
    return this.httpClient.put<IApplicationSummary>(url, data)
  }

  /**
   * Get the UC as HTML
   */
  public getHtml(personNummer: string): Observable<any> {
    const url = `${environment.loanServiceUrl}/reports/${personNummer}`
    return this.httpClient.get<any>(url)
  }

  public getOffices() {
    const url = `${environment.commonServiceUrl}/data/offices`
    return this.httpClient.get<string[]>(url).pipe(
      tap(offices => {
        this.offices.set(offices.filter(office =>
          !excludedOffices.includes(office))
          .sort((a, b) => a.localeCompare(b, 'sv-SE')))
      })
    )
  }

  public addNotes(id: string, log: IApplicationLog): Observable<IApplication> {
    const url = `${environment.loanServiceUrl}/promise/${id}/notes`
    return this.httpClient.put<IApplication>(url, log).pipe(
      tap(a => this.application.set(a))
    )
  }

  public fetchLoanDocument(id: string): Observable<string> {
    const url = `${environment.loanServiceUrl}/documents/${id}`
    return this.httpClient.get<any>(url).pipe(
      map(r => r.url)
    )
  }

  /**
   * Fetch a list of users and save them all in a map with their personnummer and themselves.
   * Also, update the "users" signal with the filtered users to use in some parts of the app.
   */
  public getUsers() {
    const url = `${environment.commonServiceUrl}/users`
    return this.httpClient.get<SparbankenUser[]>(url).pipe(
      tap(users => {
        users.forEach(user => {
          this.allUsersMap.set(user.sub, user)
        })
        const filtered = users
          .filter(user => user.office &&
            !excludedOffices.includes(user.office))
          .sort((a, b) => a.name.localeCompare(b.name, 'sv-SE'))
        this.users.set(filtered)
      })
    )
  }
}
